/**
 * Microsoft Authentication Hook
 * Provides authentication methods and state for Azure AD SSO
 */

import { useCallback } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, isMsalConfigured } from '@/lib/msalConfig';
import { createLoginNonce } from '@/lib/microsoftTokenExchange';

interface MicrosoftUser {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
}

interface UseMicrosoftAuthReturn {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  isMsalConfigured: boolean;
  user: MicrosoftUser | null;

  // Authentication methods
  loginWithRedirect: () => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Hook for Microsoft authentication
 */
export function useMicrosoftAuth(): UseMicrosoftAuthReturn {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || null);

  // Check if MSAL is configured
  const msalConfigured = isMsalConfigured();

  // Extract user info from account
  const user: MicrosoftUser | null = account
    ? {
        id: account.localAccountId,
        email: account.username,
        name: account.name || account.username,
        firstName: account.name?.split(' ')[0],
        lastName: account.name?.split(' ').slice(1).join(' '),
      }
    : null;

  /**
   * Login with redirect (for mobile/Safari)
   */
  const loginWithRedirect = useCallback(async () => {
    if (!msalConfigured) {
      throw new Error('Microsoft authentication is not configured');
    }

    try {
      // The raw nonce survives the redirect in localStorage; MicrosoftCallback
      // completes the Supabase exchange when we land back on /auth/callback.
      const hashedNonce = await createLoginNonce();
      await instance.loginRedirect({ ...loginRequest, nonce: hashedNonce });
    } catch (error: any) {
      console.error('[useMicrosoftAuth] Redirect login error:', error);
      throw error;
    }
  }, [instance, msalConfigured]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    if (!msalConfigured) return;

    try {
      // Clear all MSAL accounts
      const allAccounts = instance.getAllAccounts();
      for (const acct of allAccounts) {
        try {
          await instance.logoutPopup({
            postLogoutRedirectUri: window.location.origin + '/login',
            account: acct,
          });
        } catch (popupErr) {
          console.warn('[useMicrosoftAuth] Popup logout failed, clearing cached account locally:', popupErr);
          // setActiveAccount(null) does NOT remove the cached account —
          // clearCache actually purges it so the next visitor can't reuse it
          await instance.clearCache({ account: acct });
        }
      }
    } catch (error: any) {
      console.error('[useMicrosoftAuth] Logout error:', error);
      // Don't throw - we still want the Supabase signOut to proceed
    }
  }, [instance, account, msalConfigured]);

  return {
    isAuthenticated: msalConfigured ? isAuthenticated : false,
    isLoading: inProgress !== InteractionStatus.None,
    isMsalConfigured: msalConfigured,
    user,
    loginWithRedirect,
    logout,
  };
}

export default useMicrosoftAuth;
